<template>
    <form class="location-form">
      <b-field label="Company Name">
        <location-search v-if="allowCompanySearch"
                         @selected="selectSavedLocation"
                         v-model="facility.companyRolodexJobLocationName"
                         search-type="locations" />
        <b-input v-else v-model="facility.companyRolodexJobLocationName" />
      </b-field>

      <address-lookup v-model="facility.address"/>

    </form>
</template>

<script>
import { debounce, isEqual, isEmpty } from 'lodash-es'
import hasEmptyValues from '@/helpers/hasEmptyValues'
import LocationSearch from '@/components/RolodexLocationSearch'

import AddressLookup from '@/components/AddressLookup'

const DEBOUNCE_TIME = 300
const defaultFacilityProp = () => ({
  address: {
    street1: '',
    street2: '',
    zip: '',
    state: '',
    city: '',
    lat: null,
    lng: null
  },

  companyRolodexJobLocationName: null,
  CompanyRolodexJobLocationAddressKey: null
})

export default {
  name: 'FormLocation',

  data() {
    return {
      isDirtyAddress: true,
      unwatchAddress: null,
      facility: defaultFacilityProp(),
      isFetching: false,
      searchString: '',
      searchResults: []
    }
  },

  created() {
    this.copyValuePropToData()
  },

  props: {
    allowCompanySearch: {
      type: Boolean,
      default: true
    },
    value: {
      type: Object,
      default: defaultFacilityProp
    }
  },

  components: {
    LocationSearch,
    AddressLookup
  },

  methods: {
    /**
     * Populates the form with data from a previously saved location
     *
     * @param {Object} savedLocation The selected location
     */
    selectSavedLocation(savedLocation) {
      if (this.unwatchAddress) {
        this.unwatchAddress()
      }

      this.setDirtyAddressFlag(false)
      const facilityAddress = savedLocation.companyRolodexJobLocationAddresses[0]

      this.facility.CompanyRolodexJobLocationAddressKey = facilityAddress.companyRolodexJobLocationAddressKey
      this.facility.companyRolodexJobLocationName = savedLocation.companyRolodexJobLocationName

      // need to map these to generic names so that it can be easily fed to the `address-lookup` component
      this.facility.address = {
        CompanyRolodexJobLocationAddressKey: facilityAddress.companyRolodexJobLocationAddressKey,
        street1: facilityAddress.companyRolodexJobLocationAddressStreet1,
        street2: facilityAddress.companyRolodexJobLocationAddressStreet2,
        zip: facilityAddress.companyRolodexJobLocationAddressZip,
        state: facilityAddress.companyRolodexJobLocationAddressState,
        city: facilityAddress.companyRolodexJobLocationAddressCity,
        lat: facilityAddress.companyRolodexJobLocationGpsLatitudeCoordinate,
        lng: facilityAddress.companyRolodexJobLocationGpsLongitudeCoordinate
      }

      // when a saved address is selected, setup a watcher to know if the user manually changes it
      window.setTimeout(() => {
        this.setupAddressWatcher()
      }, 500)
    },

    /**
     * Inform parent of data change
     * @emits input
     */
    notify: debounce(function() {
      const hasContent = !hasEmptyValues(this.facility)
      const haveDifferentValues = !isEqual(this.facility, this.value)

      if (hasContent && haveDifferentValues) {
        this.$emit('input', this.payload)
      }
    }, DEBOUNCE_TIME),

    /**
     * When the passed in `value` prop gets updated... copy its value to the local state
     */
    copyValuePropToData() {
      if (this.unwatchAddress) {
        this.unwatchAddress()
      }

      this.setDirtyAddressFlag(false)
      const defaults = defaultFacilityProp()
      this.facility = isEmpty(this.value) ? defaults : this.propMapping
      this.setupAddressWatcher()
    },

    /**
     * Manually setup a watcher to know if the user types in a new address.
     * Ultimately this sets the `isDirtyAddress`
     */
    setupAddressWatcher() {
      this.unwatchAddress = this.$watch('facility.address', {
        deep: true,
        handler() {
          // setting this to null is sort of a hack... for some reason the computed properties do not immediately update
          // when changing the `setDirtyAddressFlag` flag... so we manually clear the addressKey
          this.facility.CompanyRolodexJobLocationAddressKey = null
          this.setDirtyAddressFlag(true)
        }
      })
    },

    /**
     * Sets flag to indicate if the user has manually changed the address or not
     */
    setDirtyAddressFlag(flagValue) {
      this.isDirtyAddress = flagValue
    }
  },

  computed: {
    /**
     * Takes the current `value` prop (aka: what the parent passes in via `v-model` and map its values back to something
     * that can be used and understood by the component.
     * Basically a reverse of `payload()`
     */
    propMapping() {
      const incomingValue = this.value

      const mapped = Object.assign({}, defaultFacilityProp(), incomingValue)

      const incomingValueAddress =
        incomingValue.companyRolodexJobLocationAddresses || incomingValue.companyRolodexJobLocation || {}

      mapped.address = {
        zip: incomingValueAddress.companyRolodexJobLocationAddressZip,
        state: incomingValueAddress.companyRolodexJobLocationAddressState,
        city: incomingValueAddress.companyRolodexJobLocationAddressCity,
        street1: incomingValueAddress.companyRolodexJobLocationAddressStreet1,
        street2: incomingValueAddress.companyRolodexJobLocationAddressStreet2,
        lat: incomingValueAddress.companyRolodexJobLocationGpsLatitudeCoordinate,
        lng: incomingValueAddress.companyRolodexJobLocationGpsLongitudeCoordinate,
        dock: incomingValueAddress.dock
      }

      return mapped
    },

    /**
     * Cleaned up object in a format that is intended to be sent up to the parent (aka: its final form).
     *
     * @returns {Object}
     */
    payload() {
      const facility = {
        ...this.facility
      }
      delete facility.address

      if (this.isDirtyAddress) {
        facility.CompanyRolodexJobLocationAddressKey = null
      }

      // map the generic address names back to rolodex-specific ones
      facility.companyRolodexJobLocationAddresses = {
        companyRolodexJobLocationAddressZip: this.facility.address.zip,
        companyRolodexJobLocationAddressState: this.facility.address.state,
        companyRolodexJobLocationAddressCity: this.facility.address.city,
        companyRolodexJobLocationAddressStreet1: this.facility.address.street1,
        companyRolodexJobLocationAddressStreet2: this.facility.address.street2,
        companyRolodexJobLocationGpsLatitudeCoordinate: this.facility.address.lat,
        companyRolodexJobLocationGpsLongitudeCoordinate: this.facility.address.lng,
        dock: this.facility.address.dock
      }

      return facility
    }
  },

  watch: {
    value: {
      deep: true,
      handler() {
        if (!isEqual(this.payload, this.value)) {
          this.copyValuePropToData()
        }
      }
    },

    facility: {
      deep: true,
      handler: 'notify'
    }
  }
}
</script>

<style scoped>
.location-form {
  background-color: white;
}

.subtitle {
  border-top: 1px solid #e3e3e3;
  margin-top: 12px;
  padding-top: 5px;
  margin-bottom: 12px;
}
</style>
